// backlog-reviews.ts — Endpoints de la revue de backlog (août 2026).
//
// L'API de revue MARQUE, elle n'écrit jamais dans le backlog : appliquer une
// recommandation passe par les endpoints d'action existants (fusion, statut,
// RACI), donc par leurs permissions et leur audit. Rien ici ne touche une
// action, à une exception près — le marqueur « replanification justifiée »,
// qui est porté par l'action et gardé par `actions.write`.
import { downloadGetBlob, req, upload } from './_internals.js';

export const backlogReviewsApi = {
  getBacklogReviews: (params?: { status?: string }) =>
    req('GET', '/backlog-reviews' + (params?.status ? '?status=' + params.status : '')),
  getBacklogReview: (id: number | string) => req('GET', '/backlog-reviews/' + id),
  createBacklogReview: (d: unknown) => req('POST', '/backlog-reviews', d),
  // Le SEUL réglage modifiable après création : le plafond d'affichage. Titre
  // et périmètre identifient la revue et sont figés.
  patchBacklogReview: (id: number | string, d: unknown) =>
    req('PATCH', '/backlog-reviews/' + id, d),
  deleteBacklogReview: (id: number | string) => req('DELETE', '/backlog-reviews/' + id),

  // Le geste de tri. `pile` et `to_arbitrate` sont indépendants : on peut
  // envoyer l'un, l'autre, ou les deux. `row_version` porte le verrou optimiste
  // — l'écran l'envoie toujours, pour qu'un collègue qui a tranché entre-temps
  // ne se fasse pas écraser en silence.
  patchBacklogRecommendation: (reviewId: number | string, rid: number | string, d: unknown) =>
    req('PATCH', '/backlog-reviews/' + reviewId + '/recommendations/' + rid, d),

  // Itération : relance une passe de détection sur l'état COURANT. Réponse
  // `202` — le calcul vit côté serveur, pas dans l'onglet. Le suivi passe par
  // `getBacklogRun`, charge utile minimale interrogée toutes les 3 s.
  iterateBacklogReview: (id: number | string) => req('POST', '/backlog-reviews/' + id + '/iterate'),
  getBacklogRun: (id: number | string) => req('GET', '/backlog-reviews/' + id + '/run'),
  cancelBacklogRun: (id: number | string, runId: number | string) =>
    req('POST', '/backlog-reviews/' + id + '/runs/' + runId + '/cancel'),

  // Le dossier pseudonymisé — ce qui SORTIRAIT. Aucun appel à un fournisseur
  // n'est fait ici : pas de crédit, pas de consentement au transfert hors UE à
  // vérifier. Un tenant qui a refusé le transfert garde toute la
  // fonctionnalité par le kit.
  getBacklogDossier: (id: number | string) => req('GET', '/backlog-reviews/' + id + '/dossier'),
  downloadBacklogKit: (id: number | string, filename: string) =>
    downloadGetBlob('/backlog-reviews/' + id + '/dossier?format=kit', filename),

  // Le retour du kit. Pas de `202` ici : rien n'est envoyé nulle part, donc
  // rien à attendre — la validation et l'écriture tiennent dans la requête, et
  // le bilan (`loaded` / `rejected` / `reasons`) revient avec la réponse.
  // Toujours aucun crédit, toujours aucun consentement au transfert hors UE.
  importBacklogRecommendations: (id: number | string, form: FormData) =>
    upload('/backlog-reviews/' + id + '/import', form),

  // Itération IA : même 202, même run, même suivi que l'itération procédurale.
  // Les 10 crédits sont vérifiés AVANT le départ et décomptés SEULEMENT à la
  // réussite — un échec ne coûte rien, et il n'y a jamais de débit suivi d'un
  // remboursement.
  analyzeBacklogReview: (id: number | string) => req('POST', '/backlog-reviews/' + id + '/analyze'),

  // Support de comité. La création ENREGISTRE la sélection ; le binaire, lui,
  // se régénère à chaque export — c'est ce qui garantit qu'aucune slide ne
  // s'appuie sur une action disparue depuis la composition. La réponse dit ce
  // qui a été retiré, et le plan tel que le serveur le rendra.
  createBacklogSupport: (reviewId: number | string, d: unknown) =>
    req('POST', '/backlog-reviews/' + reviewId + '/supports', d),
  exportBacklogSupport: (reviewId: number | string, sid: number | string, filename: string) =>
    downloadGetBlob('/backlog-reviews/' + reviewId + '/supports/' + sid + '/export', filename),

  // Retour de comité, saisi par LOT : un comité tranche une série, et une
  // revue à moitié tranchée après un rechargement serait pire qu'une saisie à
  // refaire.
  postCommitteeFeedback: (reviewId: number | string, d: unknown) =>
    req('POST', '/backlog-reviews/' + reviewId + '/committee-feedback', d),

  // Les trois sorties d'aval. Le journal est disponible dès la première
  // application — le PMO diffuse au fil de l'eau, pas seulement à la clôture.
  downloadBacklogDecisions: (id: number | string, filename: string) =>
    downloadGetBlob('/backlog-reviews/' + id + '/exports/decisions.xlsx', filename),
  getArbitrationRecipients: (id: number | string) =>
    req('GET', '/backlog-reviews/' + id + '/arbitration-recipients'),
  requestArbitration: (id: number | string, d: unknown) =>
    req('POST', '/backlog-reviews/' + id + '/request-arbitration', d),
  // `409` tant que la revue n'est pas clôturée : un bilan sur une revue en
  // cours mesurerait une décision qui n'a pas encore eu lieu.
  getBacklogOutcome: (id: number | string) => req('GET', '/backlog-reviews/' + id + '/outcome'),

  // Marqueur porté par l'ACTION, valable pour toutes les revues à venir.
  setActionReviewFlag: (actionId: number | string, d: unknown) =>
    req('POST', '/backlog-reviews/actions/' + actionId + '/review-flags', d),
};
